import { environment } from '../../../../environments/environment';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { LocalStorageService } from '../localStorage/local-storage.service';
import { AuthStateService } from '../auth-state/auth-state-service.service';
import { JwtService, JwtPayload } from '../jwt/jwt.service';
import { UserService } from '../user/user.service';
import { DynamicRouteService } from '../dynamic-route/dynamic-route.service';
import { SessionManagerService } from '../session-manager/session-manager.service';
import { TIME_CONSTANTS, TimeUtils } from '../../constants/time.constants';

export type LoginMode = 'replace' | 'add';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private apiUrl = environment.apiURL;

  constructor(
    private http: HttpClient,
    private router: Router,
    private localStorageService: LocalStorageService,
    private authStateService: AuthStateService,
    private jwtService: JwtService,
    private userService: UserService,
    private dynamicRouteService: DynamicRouteService,
    private sessionManager: SessionManagerService
  ) { }

  login(credentials: LoginCredentials, mode: LoginMode = 'replace'): Observable<AuthResponse> {
    const body: any = { email: credentials.email, password: credentials.password };

    if (mode === 'add') {
      // In add mode, use per-session device_token for this email
      const sessionDeviceToken = this.sessionManager.getDeviceTokenForEmail(credentials.email);
      if (sessionDeviceToken) {
        body.device_token = sessionDeviceToken;
      }
    } else {
      const deviceToken = this.getDeviceToken();
      if (deviceToken) {
        body.device_token = deviceToken;
      }
    }

    return this.http.post<AuthResponse>(this.apiUrl + 'auth', body);
  }

  handleSuccessfulLogin(response: AuthResponse, mode: LoginMode = 'replace', email?: string): void {
    const loginEmail = email || '';

    if (mode === 'add') {
      this.sessionManager.addSession(response.access_token!, response.device_token ?? null, loginEmail);
    } else {
      this.sessionManager.replaceActiveSession(response.access_token!, response.device_token ?? null, loginEmail);
      // Keep global device_token for backward compatibility on first/normal login
      if (response.device_token) {
        this.setDeviceToken(response.device_token);
      }
    }

    this.dynamicRouteService.clearDynamicRoutes();
    const dynamicRoutes = this.authStateService.getRoutesItems();
    this.dynamicRouteService.configureDynamicRoutes(dynamicRoutes);
  }

  verifyMfa(payload: MfaVerifyRequest): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(this.apiUrl + 'auth/mfa/verify', payload);
  }

  resendMfa(challengeToken: string): Observable<MfaResendResponse> {
    return this.http.post<MfaResendResponse>(this.apiUrl + 'auth/mfa/resend', {
      challenge_token: challengeToken,
    });
  }

  isMfaRequired(response: AuthResponse): boolean {
    return !!response.data?.mfa_required;
  }

  getDeviceToken(): string | null {
    return this.localStorageService.getItem('device_token');
  }

  setDeviceToken(token: string): void {
    this.localStorageService.setItem('device_token', token);
  }

  clearDeviceToken(): void {
    this.localStorageService.removeItem('device_token');
  }

  private storeUserData(userData: JwtPayload): void {
    const expires_in = TimeUtils.addDays(new Date(), 1);

    this.localStorageService.setItem('expires_in', expires_in.getTime().toString());
    this.localStorageService.setItem('name', userData.name);
    this.localStorageService.setItem('role', userData.role);
    this.localStorageService.setItem('account_type', userData.account_type);
    this.localStorageService.setItem('account_name', userData.account_name);
    this.localStorageService.setItem('account_reference', userData.account_reference);
    this.localStorageService.setItem('user_reference', userData.user_reference);
    this.localStorageService.setItem('assigned_smtp', userData.assigned_smtp || '');
    this.localStorageService.setItem('smtp_from', userData.smtp_from || '');
  }

  logout(): void {
    // Call backend logout to invalidate the active JWT
    const token = this.localStorageService.getToken();
    if (token) {
      this.http.get(this.apiUrl + 'logout').subscribe({ error: () => {} });
    }

    this.sessionManager.removeActiveSession();
    this.userService.clearUser();

    const remaining = this.sessionManager.getSessions();
    if (remaining.length > 0) {
      const nextIndex = this.sessionManager.getActiveIndex();
      const switched = this.sessionManager.switchToSession(nextIndex);
      if (switched) {
        this.router.navigateByUrl(this.authStateService.getRedirectUrl());
      } else {
        // Next session also expired — go to login
        this.dynamicRouteService.clearDynamicRoutes();
        this.router.navigate(['/login']);
      }
    } else {
      this.dynamicRouteService.clearDynamicRoutes();
      this.router.navigate(['/login']);
    }
  }
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface AuthResponse {
  success: boolean;
  access_token?: string;
  token_type?: string;
  expires_in?: number;
  assigned_smtp?: string | null;
  smtp_from?: string | null;
  device_token?: string;
  data?: {
    mfa_required?: boolean;
    challenge_token?: string;
  };
  message?: any;
}

export interface MfaVerifyRequest {
  challenge_token: string;
  code: string;
  remember_device?: boolean;
  device_label?: string;
}

export interface MfaResendResponse {
  success: boolean;
  data: null;
  message?: any;
}

/** @deprecated Use AuthResponse instead */
export interface LoginResponse {
  access_token: string;
  expires_in?: number;
  token_type?: string;
}
